<template>
  <Listbox v-model="selected" as="div" @update:model-value="$emit('update:modelValue', selected)">
    <div class="flex items-center">
      <ListboxLabel :for="id" class="block text-sm font-medium leading-6">{{ modelName }}</ListboxLabel>
      <Tooltip v-if="modelTitle" class="ml-1" :aria-label="modelAriaLabel" :text="modelTitle" :focusable="true">
        <QuestionMarkCircleIcon class="h-4 w-4 text-muted-foreground" />
      </Tooltip>
    </div>
    <div class="relative mt-2">
      <ListboxButton
        :id="id"
        class="relative w-full cursor-default rounded-md py-1.5 pl-3 pr-10 text-left shadow-sm ring-1 ring-inset ring-[#ccd1c4] focus:outline-none focus:ring-2 focus:ring-[#e05454] sm:text-sm sm:leading-6"
      >
        <span v-if="!selected || Object.keys(selected).length === 0" class="opacity-50">{{ placeholder }}</span>
        <span v-else class="block truncate">{{ getKey(selected, modelType) }}</span>
        <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon class="h-5 w-5" aria-hidden="true" />
        </span>
      </ListboxButton>

      <ListboxOptions
        class="absolute z-10 mt-1 max-h-60 w-full divide-y divide-[#e05454] overflow-auto rounded-md border border-[#e05454] bg-background py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
      >
        <ListboxOption as="template" v-for="obj in modelList" :key="obj" :value="obj" v-slot="{ active, selected }">
          <li :class="[active ? 'bg-primary text-white' : '', 'relative cursor-default select-none py-2 pl-3 pr-9']">
            <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{
              getKey(obj, modelType)
            }}</span>

            <span
              v-if="selected"
              :class="[active ? 'text-white' : 'text-[#e05454]', 'absolute inset-y-0 right-0 flex items-center pr-4']"
            >
              <CheckIcon class="h-5 w-5" aria-hidden="true" />
            </span>
          </li>
        </ListboxOption>
      </ListboxOptions>
    </div>
  </Listbox>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/vue/24/solid";
import { QuestionMarkCircleIcon } from "@heroicons/vue/24/outline";
import Tooltip from "./ui/Tooltip.vue";

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelName: {
    type: String,
    required: true,
  },
  modelTitle: {
    type: String,
  },
  modelAriaLabel: {
    type: String,
  },
  selectedValue: {
    required: true,
  },
  placeholder: {
    type: String,
    default: "...",
  },
  modelList: {
    required: true,
  },
  modelType: {
    default: null,
  },
});

defineEmits(["update:modelValue"]);

const selected = ref(props.selectedValue);

const getKey = (obj, prop) => {
  if (prop === null) {
    return obj;
  }

  return obj[prop];
};

onMounted(() => {
  if (Boolean(props.modelTitle) !== Boolean(props.modelAriaLabel)) {
    console.warn("You must provide either neither of or both of `modelTitle` and `modelAriaLabel`");
  }
});
</script>
